"use client";

import { useEffect } from "react";
import { log } from "@/lib/logger";
import { reportClientError } from "@/lib/observability/report-client-error";
import {
  isStaleDeploymentError,
  reloadOnceForStaleDeployment,
} from "@/lib/observability/stale-deployment";

/**
 * Error boundary de niveau route : remplace la page blanche brute
 * (« Application error: a client-side exception ») par un écran de récupération
 * propre, et trace l'erreur réelle (console + /api/client-error) pour pouvoir
 * diagnostiquer les crashs intermittents en prod.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    log.error("Erreur de rendu (page)", {
      message: error.message,
      digest: error.digest,
      stack: error.stack,
    });
    reportClientError(error, "page");
    // Onglet ouvert avant un déploiement : le bundle référence une Server
    // Action disparue. Un rechargement récupère la version à jour et évite
    // d'afficher un écran d'erreur pour un simple décalage de version.
    if (isStaleDeploymentError(error)) reloadOnceForStaleDeployment();
  }, [error]);

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-bg px-6 py-16 text-center">
      <div className="w-full max-w-[420px]">
        <div className="mx-auto mb-7 flex h-16 w-16 items-center justify-center rounded-full bg-gold-soft text-gold-deep">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <h1 className="mb-2.5 font-serif text-[30px] font-normal leading-[1.1] text-ink md:text-[34px]">
          Une erreur est survenue
        </h1>
        <p className="mb-8 text-[14px] leading-[1.6] text-body">
          Quelque chose n&apos;a pas fonctionné de notre côté. Réessayez — vos
          informations sont bien enregistrées.
        </p>
        <button
          type="button"
          onClick={reset}
          className="inline-flex w-full items-center justify-center rounded-pill bg-gold px-6 py-3.5 text-[13px] font-semibold uppercase tracking-wider text-ink transition-colors hover:bg-gold-deep"
        >
          Réessayer
        </button>
      </div>
    </main>
  );
}
